/**
 * Склейка нескольких манифестов атласов в один: заглушка (`placeholder`) + настоящие атласы конвейера (`voxel`).
 * Картинки идут подряд (индексы кадров сдвигаются), кадры и анимации более поздних атласов перекрывают ранние с тем же
 * именем — так модель из `tools/voxel` заменяет кубик-заглушку только для своей сущности. `tilePixels` и прочие поля
 * общего манифеста — от первого атласа; свой масштаб каждого кадра — в `frameTilePixels`.
 */
import type { AtlasManifest } from "@newfungame/contracts";

export interface MergedManifest {
  manifest: AtlasManifest;
  /** `tilePixels` атласа, из которого взят кадр. */
  frameTilePixels: Map<string, number>;
  /** Из какого атласа (индекс во входном списке) взята каждая картинка. */
  imageSource: number[];
}

export function mergeManifests(list: readonly AtlasManifest[]): MergedManifest {
  const [first] = list;
  if (!first) throw new Error("mergeManifests: пустой список");
  const images: AtlasManifest["images"] = [];
  const frames: AtlasManifest["frames"] = {};
  const animations: AtlasManifest["animations"] = {};
  const frameTilePixels = new Map<string, number>();
  const imageSource: number[] = [];
  list.forEach((m, src) => {
    const offset = images.length;
    for (const img of m.images) {
      images.push(img);
      imageSource.push(src);
    }
    for (const [name, f] of Object.entries(m.frames)) {
      frames[name] = { ...f, image: f.image + offset };
      frameTilePixels.set(name, m.tilePixels);
    }
    Object.assign(animations, m.animations);
  });
  return { manifest: { ...first, images, frames, animations }, frameTilePixels, imageSource };
}

/**
 * Дозагрузка: к уже загруженному атласу добавить ещё один (чиби-атласы после первого кадра, MADR 0017). Кадры и анимации
 * `more` перекрывают одноимённые в `base`; `base` не меняется.
 */
export function mergeLoaded<F>(
  base: { manifest: AtlasManifest; frames: ReadonlyMap<string, F> },
  more: { manifest: AtlasManifest; frames: ReadonlyMap<string, F> },
): { manifest: AtlasManifest; frames: Map<string, F> } {
  return {
    manifest: { ...base.manifest, animations: { ...base.manifest.animations, ...more.manifest.animations } },
    frames: new Map([...base.frames, ...more.frames]),
  };
}

/**
 * Адрес картинки атласа с версией из манифеста (ISSUE-129). Картинка кешируется браузером на 4 ч под тем же именем, а
 * манифест — нет: после пересборки атласа вернувшийся игрок резал бы старую картинку новыми рамками кадров.
 */
export function imageUrl(base: string, img: AtlasManifest["images"][number]): string {
  return `${base}${img.webp ?? img.file}?v=${img.sha256.slice(0, 12)}`;
}
